<template>
  <div class="screen eind">
    <StatusBar
      :week="store.weekNumber"
      :weeks-total="store.weeksTotal"
      :stars="store.starBalance"
      action-label="Spelen"
      :action-disabled="true"
    />

    <div
      ref="sheetEl"
      class="eind__sheet"
      :style="{ '--burst-centre': burstCentre + 'px', '--burst-span': burstSpan + 'px' }"
    >
      <!-- the sunburst is an <img> so its convergence point can be pinned to the
           centre of the neon sign, exactly as on the intro and the home screen -->
      <img :src="burstImg" class="eind__burst" alt="" aria-hidden="true" />
      <div class="eind__fade" aria-hidden="true" />

      <div class="eind__body">
        <img ref="signEl" :src="signImg" class="eind__sign" alt="Het Priority Spel" />
        <h1 class="eind__title">Spannend!</h1>
        <p class="eind__lead">
          Je trekking vindt plaats na de 7e week van een ronde. Tot die tijd kun je elke week
          terugkomen om meer sterren te verdienen!
        </p>

        <div class="eind__card">
          <h2>Deze sterren heb je al ingezet</h2>
          <ul v-if="entries.length" class="eind__rows">
            <li v-for="e in entries" :key="e.id">
              <span>{{ e.title }}</span>
              <span class="eind__val"><StarIcon :size="17" :filled="false" />{{ e.stars }}</span>
            </li>
          </ul>
          <p v-else class="eind__empty">Je hebt deze cyclus nog geen sterren ingezet.</p>

          <div class="eind__divider" />
          <!-- weeks the player actually PLAYED in, not the round's week number -->
          <div class="eind__weeks">
            <span>Aantal meegespeelde weken</span>
            <strong>{{ store.weeksPlayed ?? '–' }}/{{ store.weeksTotal }}</strong>
          </div>
        </div>
      </div>

      <div class="screen__footer">
        <button class="eind__cta" type="button" @click="goHome">Terug naar Priority</button>

        <div v-if="isDev" class="eind__dev">
          <button @click="dev('week')">↻ week</button>
          <button @click="dev('cycle')">↻ cyclus</button>
          <button @click="dev('reset')">✖ reset</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import StatusBar from '@/components/game/StatusBar.vue'
import StarIcon from '@/components/ui/StarIcon.vue'
import signImg from '@/assets/v3/priority-sign.webp'
import burstImg from '@/assets/v3/sunburst-bg.webp'

const store = useGameStore()
const router = useRouter()
const isDev = import.meta.env.DEV

// --- Sunburst anchor --------------------------------------------------------
// Figma 421:2147 converges the sunburst on the neon sign (convergence 193/288.75,
// sign centre 191.5/285.5). `background-size: cover` can only pin that point to a
// fixed FRACTION of the box, so the burst is an <img> hung on a measured anchor —
// the same move as the intro, the home reel and the game-over sign. The sheet's
// own height is measured too, because this screen is taller than the viewport and
// 100dvh would leave the bottom of the burst short.
const sheetEl = ref(null)
const signEl = ref(null)
const burstCentre = ref(300)
const burstSpan = ref(900)

function fitBurst() {
  const sheet = sheetEl.value
  const sign = signEl.value
  if (!sheet || !sign) return
  const s = sheet.getBoundingClientRect()
  const g = sign.getBoundingClientRect()
  if (!s.height || !g.height) return
  burstSpan.value = s.height
  burstCentre.value = g.top + g.height / 2 - s.top
}

let ro = null
onMounted(() => {
  if (!store.prizes.length) store.loadShop()
  fitBurst()
  if (typeof ResizeObserver !== 'function') return
  // the sheet grows with the summary rows; the sign resizes again when it decodes
  ro = new ResizeObserver(fitBurst)
  ro.observe(sheetEl.value)
  ro.observe(signEl.value)
})
onBeforeUnmount(() => ro?.disconnect())

const entries = computed(() => {
  const bets = store.cycle?.prizeBets || {}
  return Object.entries(bets)
    .filter(([, n]) => n > 0)
    .map(([id, stars]) => ({
      id,
      stars,
      title: store.prizes.find((p) => p.id === id)?.title || id,
    }))
})

function goHome() {
  router.replace({ name: 'home' })
}
async function dev(kind) {
  if (kind === 'week') await store.devWeeklyReset()
  else if (kind === 'cycle') await store.devCycleReset()
  else await store.devReset()
  router.replace({ name: 'home' })
}
</script>

<style lang="scss" scoped>
.eind {
  // white backing so the sheet's rounded top corners read against white (like home)
  background: var(--c-white);
  color: var(--c-on-brand);
}
.eind__sheet {
  // both set from fitBurst(); these are only the pre-measure fallbacks
  --burst-centre: 300px;
  --burst-span: 900px;

  position: relative;
  overflow: hidden; // clip the oversized sunburst to the rounded top corners
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  // Figma 421:2147 — rounded top corners (like home 426:286)
  background: var(--c-red);
  border-radius: 45px 45px 0 0;
}

// Sized so it still reaches every edge of the sheet once its convergence point
// sits on the sign (A). With 40.95% of the image above that point:
//   top edge    <= 0      ->  H >= A / 0.4095
//   bottom edge >= bottom ->  H >= (span - A) / 0.5905
//   never narrower than the column
//                         ->  H >= column / 0.566314 = 1.7658 * column
.eind__burst {
  position: absolute;
  left: 50%; // the sign is centred in the column
  top: var(--burst-centre);
  width: auto;
  max-width: none;
  height: calc(
    1.015 *
      max(
        var(--burst-centre) / 0.4095,
        (var(--burst-span) - var(--burst-centre)) / 0.5905,
        1.7658 * min(100vw, var(--app-max-width))
      )
  );
  // put the image's convergence point (49.4% / 40.95%) on the sign's centre
  transform: translate(-49.4%, -40.95%);
  pointer-events: none;
}

// Figma 421:2153 — the rays fade out below the sign so the white summary card
// and the CTA read against solid red.
.eind__fade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(230, 0, 0, 0) 42%, var(--c-red) 70%);
  pointer-events: none;
}
// The status bar is the top element now that the header is gone.
.eind :deep(.status-bar) {
  padding-top: max(env(safe-area-inset-top), var(--sp-3));
}
.eind__body {
  position: relative; // above the sunburst
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--sp-4) var(--sp-6);
}
.eind__sign {
  width: 240px; // tight-cropped asset → this is the visible neon width (Figma ~220px)
  height: auto;
  margin: var(--sp-6) 0 var(--sp-7); // gap before the title
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.35));
}
.eind__title {
  font-size: 23px; // Figma 421:2154
}
.eind__lead {
  max-width: 286px; // Figma 421:2155
  margin-top: var(--sp-3);
  font-size: var(--fs-lg);
  line-height: 23px;
  color: var(--c-white); // Figma — solid white, not soft
}
.eind__card {
  width: 100%;
  max-width: 318px;
  margin-top: var(--sp-6);
  padding: var(--sp-5);
  text-align: left;
  color: var(--c-ink);
  background: var(--c-white);
  border-radius: 24px;

  h2 {
    font-size: var(--fs-lg);
    text-align: center;
    margin-bottom: var(--sp-4);
  }
}
.eind__rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-3);
    font-size: var(--fs-md);
  }
}
.eind__val {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: var(--fw-bold);
}
.eind__empty {
  color: var(--c-muted);
}
.eind__divider {
  height: 1px;
  margin: var(--sp-4) 0;
  background: var(--c-border);
}
.eind__weeks {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--fs-md);
}
.eind .screen__footer {
  position: relative; // above the sunburst
}
.eind__cta {
  display: block;
  width: 100%;
  max-width: 318px; // Figma 421:2197 — aligns with the 318px summary card
  height: 60px;
  margin: 0 auto;
  font-family: var(--font-brand);
  font-size: var(--fs-xl);
  font-weight: var(--fw-bold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border: none;
  border-radius: var(--r-lg);
}
.eind__dev {
  display: flex;
  gap: var(--sp-2);
  justify-content: center;
  margin-top: var(--sp-3);

  button {
    font-size: var(--fs-xs);
    padding: 6px 10px;
    color: var(--c-white);
    background: rgba(0, 0, 0, 0.28);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--r-pill);
  }
}
</style>
